import { FileContainer, process, saveMiddlewareConfiguration } from "am-tax-fe-core";
import createClient, { Middleware } from "openapi-fetch";

import { fetchApiUrl } from "../apiUtils";
import { paths, components } from "../generated/generated-api";
import { uploadFile } from "../uploadUtils";

let configured = false;
export let cachedApiUrl: string | null = null;

(async () => {
    cachedApiUrl = await fetchApiUrl();
})();

let currentClient = createClient<paths, "application/json">({ baseUrl: "" });

//#region Dto definition
export type Entity = components["schemas"]["EntityDto"];
export type CreateEntity = components["schemas"]["CreateOrUpdateEntityDto"];
export type UpdateEntity = components["schemas"]["CreateOrUpdateEntityDto"] & { id: string };
export type Client = components["schemas"]["ClientDto"];
export type Jurisdiction = components["schemas"]["JurisdictionDto"];
export type Report = components["schemas"]["ReportDto"];
export type CreateOrUpdateReport = components["schemas"]["CreateOrUpdateReportDto"];
export type User = components["schemas"]["UserDto"];
export type UpdateSystemAdminRequest = components["schemas"]["UpdateSystemAdminRequest"];

export function configureApiClient(baseUrl: string, middlewares?: Middleware[]) {
    saveMiddlewareConfiguration(middlewares);
    currentClient = createClient<paths, "application/json">({ baseUrl });
    if (middlewares) {
        currentClient.use(...middlewares);
    }
    configured = true;
}

export function client() {
    if (!configured) {
        throw new Error("You must call configureApiClient before making any api calls");
    }
    return currentClient;
}

export const getEntityAsync = async (id: string): Promise<Entity> =>
    process(
        client().GET("/api/entities/{entityId}", {
            params: {
                path: {
                    entityId: id,
                },
            },
        }),
    );

export const getEntitiesAsync = async (): Promise<Entity[]> => process(client().GET("/api/entities"));

export const getEntitiesByClientIdAsync = async (clientId: string): Promise<Entity[]> =>
    process(
        client().GET("/api/entities/client/{clientId}", {
            params: {
                path: {
                    clientId: clientId,
                },
            },
        }),
    );

export const createEntityAsync = async (args: CreateEntity): Promise<Entity> => process(client().POST("/api/entities", { body: args }));

export const updateEntityAsync = async (args: UpdateEntity): Promise<Entity> =>
    process(
        client().PUT("/api/entities/{entityId}", {
            params: {
                path: {
                    entityId: args.id,
                },
            },
            body: args,
        }),
    );

export const deleteEntityAsync = async (id: string): Promise<Entity> =>
    process(
        client().DELETE("/api/entities/{entityId}", {
            params: {
                path: {
                    entityId: id,
                },
            },
        }),
    );

export const uploadEntityFileAsync = async (file: FileContainer, clientId: string) => {
    return uploadFile(`${cachedApiUrl}/api/entities/upload/${clientId}`, file);
};

// Client API
export const getClients = async (): Promise<Client[]> => process(client().GET("/api/clients"));

// Jurisdiction API
export const getJurisdictions = async (): Promise<Jurisdiction[]> => process(client().GET("/api/jurisdictions"));

// Report API
export const getReports = async (): Promise<Report[]> => process(client().GET("/api/reports"));
export const getReportById = async (reportId: string): Promise<Report> => process(client().GET("/api/reports/{reportId}", { params: { path: { reportId } } }));
export const createReport = async (report: CreateOrUpdateReport): Promise<Report> => process(client().POST("/api/reports", { body: report }));
export const updateReport = async (reportId: string, report: CreateOrUpdateReport): Promise<Report> =>
    process(client().PUT("/api/reports/{reportId}", { params: { path: { reportId } }, body: report }));
export const deleteReport = async (reportId: string): Promise<void> => process(client().DELETE("/api/reports/{reportId}", { params: { path: { reportId } } }));

// User API
export const getUsers = async (): Promise<User[]> => process(client().GET("/api/users"));
export const getCurrentUser = async (): Promise<User> => process(client().GET("/api/users/me"));
export const updateSystemAdminStatus = async (azureAdObjectId: string, request: UpdateSystemAdminRequest): Promise<User> =>
    process(
        client().PUT("/api/users/{azureAdObjectId}/system-admin", {
            params: {
                path: {
                    azureAdObjectId: azureAdObjectId,
                },
            },
            body: request,
        }),
    );

export const downloadEntityTemplateAsync = async (): Promise<Response> => {
    const result = await client().GET("/api/entities/download/template", {
        parseAs: "stream",
    });

    if (!result.response || !result.response.ok) {
        throw new Error(`Failed to download entity template: ${result.response?.statusText || "Unknown error"}`);
    }

    return result.response;
};
